/**
 * The file that a file command returns. The server writes such a result as the raw file, not as JSON, so there is no
 * envelope and there are no events.
 */
export interface RemoteFile {
  readonly blob: Blob
  /** The name from the `Content-Disposition` header, when the server sent one. */
  readonly fileName: string | null
  readonly contentType: string
}

/** The file name of a `Content-Disposition` header: `filename*` (RFC 5987) when it is UTF-8, otherwise `filename`. */
export function fileNameFromContentDisposition(header: string | null): string | null {
  if (header == null) return null

  const extended = /filename\*\s*=\s*utf-8'[^']*'([^;]+)/i.exec(header)
  if (extended?.[1] != null) {
    try {
      return decodeURIComponent(extended[1].trim())
    } catch {
      // Fall through to the plain file name.
    }
  }

  const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(header)
  if (quoted?.[1] != null) return quoted[1].replace(/\\(.)/g, "$1")

  const token = /filename\s*=\s*([^;\s]+)/i.exec(header)
  return token?.[1] ?? null
}

/** Reads the body of a response as a file. */
export async function readRemoteFile(response: Response): Promise<RemoteFile> {
  const blob = await response.blob()
  return {
    blob,
    fileName: fileNameFromContentDisposition(response.headers.get("Content-Disposition")),
    contentType: response.headers.get("Content-Type") ?? (blob.type || "application/octet-stream"),
  }
}

/**
 * Saves a file through the browser: an object URL on an anchor that is clicked. The name defaults to the one of the
 * server, then to "download".
 */
export function saveRemoteFile(file: RemoteFile, fileName: string = file.fileName ?? "download") {
  const url = URL.createObjectURL(file.blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName
  anchor.style.display = "none"
  document.body.append(anchor)
  try {
    anchor.click()
  } finally {
    anchor.remove()
    // The download starts from the click; the browser keeps the blob alive for it.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}
